// src/modules/time-tracking/TimeTrackingReport.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { format, subDays } from 'date-fns';
import { FaClock, FaUsers, FaUserClock, FaSyncAlt, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Select from '../../components/shared/Select';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import Pagination from '../../components/shared/Pagination';
import { StatusBadge, TableContainer, StyledTable, PageHeader, HeaderContent } from '../../components/styled/CommonStyles';
import { listEmployees } from '../employees/employeeService';
import { getReportShifts, updateShift, deleteShift, formatHours, shiftSeconds, statusColor, statusLabel } from './timeTrackingService';
import { formatDateTime } from '../communications/utils/formatUtils';
import ShiftFormModal from './ShiftFormModal';

const Filters = styled.div`
  display: flex; flex-wrap: wrap; gap: ${(p) => p.theme.spacing(2)};
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  align-items: flex-end;
`;
const DateField = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small}; }
  input { background: ${(p) => p.theme.colors.background}; color: ${(p) => p.theme.colors.textPrimary};
    border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
    padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)}; }
`;
const Metrics = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(3)};
`;
const Metric = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; box-shadow: 0 2px 4px rgba(0,0,0,.1);
  .label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small};
    display: flex; align-items: center; gap: 8px; }
  .value { color: ${(p) => p.theme.colors.primary}; font-size: 2rem; font-weight: 700; margin-top: 6px; }
  svg { color: ${(p) => p.theme.colors.primary}; }
`;
const ClickRow = styled.tr`cursor: pointer;`;
const RefreshBtn = styled.button`
  background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black};
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer;
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)};
  display: flex; align-items: center; gap: 8px; font-weight: 500;
`;
const GhostBtn = styled(RefreshBtn)`
  background: transparent; color: ${(p) => p.theme.colors.textSecondary};
  border: 1px solid ${(p) => p.theme.colors.inputGray};
`;
const RowActions = styled.div`
  display: flex; gap: 6px;
  button { background: none; border: none; cursor: pointer; padding: 6px;
    color: ${(p) => p.theme.colors.textSecondary}; border-radius: 6px; }
  button:hover { color: ${(p) => p.theme.colors.primary}; background: ${(p) => p.theme.colors.background}; }
  button.danger:hover { color: ${(p) => p.theme.colors.error}; }
`;
const ReviewCell = styled.div`
  display: flex; align-items: center; gap: 8px;
  button {
    background: ${(p) => p.theme.colors.primary}22; border: 1px solid ${(p) => p.theme.colors.primary}66;
    color: ${(p) => p.theme.colors.primary}; border-radius: 6px; cursor: pointer;
    padding: 3px 8px; font-size: 0.72rem; font-weight: 600; white-space: nowrap;
  }
  button:hover { background: ${(p) => p.theme.colors.primary}33; }
`;
const CheckField = styled.label`
  display: flex; align-items: center; gap: 8px; align-self: center;
  color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small};
  cursor: pointer; user-select: none;
  input { width: 16px; height: 16px; accent-color: ${(p) => p.theme.colors.primary}; cursor: pointer; }
`;
const HoursCaption = styled.div`
  color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.7rem; margin-top: 2px;
`;

const TimeTrackingReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [userId, setUserId] = useState(searchParams.get('userId') || '');
  const [from, setFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [status, setStatus] = useState('');
  const [reviewOnly, setReviewOnly] = useState(searchParams.get('reviewOnly') === 'true');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingShift, setEditingShift] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => {
    listEmployees().then(setEmployees).catch(() => setEmployees([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // include the whole "to" day
      const toEnd = to ? `${to}T23:59:59` : undefined;
      const data = await getReportShifts({
        userId: userId || undefined,
        from: from || undefined,
        to: toEnd,
        status: status || undefined,
        reviewOnly: reviewOnly || undefined,
        limit: perPage,
        offset: (page - 1) * perPage,
      });
      setShifts(data.shifts || []);
      setTotal(data.total ?? (data.shifts ? data.shifts.length : 0));
    } catch (e) {
      setShifts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userId, from, to, status, reviewOnly, page, perPage]);

  useEffect(() => { load(); }, [load]);

  // Any filter change returns to the first page.
  const resetPage = () => setPage(1);

  const totalSeconds = shifts.reduce((sum, s) => sum + shiftSeconds(s), 0);
  const activeCount = shifts.filter((s) => s.status === 'active').length;
  const paginated = total > shifts.length;

  const openCreate = () => { setModalMode('create'); setEditingShift(null); setModalOpen(true); };
  const openEdit = (s) => { setModalMode('edit'); setEditingShift(s); setModalOpen(true); };
  const handleSaved = (msg) => {
    setModalOpen(false);
    setFeedbackMsg(msg); setFeedbackSuccess(true); setShowFeedback(true);
    load();
  };
  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteShift(target.id);
      setFeedbackMsg('Shift deleted'); setFeedbackSuccess(true);
      load();
    } catch (e) {
      setFeedbackMsg(e.response?.data?.message || 'Failed to delete shift'); setFeedbackSuccess(false);
    } finally {
      setShowFeedback(true);
    }
  };
  const handleResolve = async (s) => {
    try {
      await updateShift(s.id, { review_flag: false });
      setFeedbackMsg('Shift marked as reviewed'); setFeedbackSuccess(true);
      load();
    } catch (e) {
      setFeedbackMsg(e.response?.data?.message || 'Failed to resolve shift'); setFeedbackSuccess(false);
    } finally {
      setShowFeedback(true);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <HeaderContent>
          <h1>Time Tracking</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <RefreshBtn onClick={openCreate}><FaPlus /> Add shift</RefreshBtn>
            <GhostBtn onClick={load}><FaSyncAlt /> Refresh</GhostBtn>
          </div>
        </HeaderContent>
      </PageHeader>

      <Filters>
        <div style={{ minWidth: 180, flex: 1 }}>
          <Select label="Employee" name="userId" value={userId}
            onChange={(e) => { setUserId(e.target.value); resetPage(); }}>
            <option value="">All employees</option>
            {employees.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
          </Select>
        </div>
        <div style={{ minWidth: 150 }}>
          <Select label="Status" name="status" value={status}
            onChange={(e) => { setStatus(e.target.value); resetPage(); }}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="auto_closed">Auto-closed</option>
          </Select>
        </div>
        <DateField><label>From</label>
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); resetPage(); }} /></DateField>
        <DateField><label>To</label>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); resetPage(); }} /></DateField>
        <CheckField>
          <input type="checkbox" checked={reviewOnly}
            onChange={(e) => { setReviewOnly(e.target.checked); resetPage(); }} />
          Pending review only
        </CheckField>
      </Filters>

      <Metrics>
        <Metric>
          <div className="label"><FaClock /> Total hours</div>
          <div className="value">{formatHours(totalSeconds)}</div>
          {paginated && <HoursCaption>this page only</HoursCaption>}
        </Metric>
        <Metric><div className="label"><FaUsers /> Shifts</div><div className="value">{total}</div></Metric>
        <Metric><div className="label"><FaUserClock /> Active now</div><div className="value">{activeCount}{paginated ? '+' : ''}</div></Metric>
      </Metrics>

      {loading ? <LoadingSpinner /> : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <th>Employee</th><th>Clock In</th><th>Clock Out</th>
                <th>Duration</th><th>Status</th><th>Review</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>No shifts in this range.</td></tr>
              ) : shifts.map((s) => (
                <ClickRow key={s.id} onClick={() => navigate(`/time-tracking/shifts/${s.id}`)}>
                  <td>{s.User?.username || `#${s.user_id}`}</td>
                  <td>{formatDateTime(s.clock_in_at)}</td>
                  <td>{s.clock_out_at ? formatDateTime(s.clock_out_at) : '—'}</td>
                  <td>{formatHours(shiftSeconds(s))}</td>
                  <td><StatusBadge color={statusColor(s.status)}>{statusLabel(s.status)}</StatusBadge></td>
                  <td>
                    {s.review_flag ? (
                      <ReviewCell>
                        <span title="Flagged for review">⚠️</span>
                        <button title="Mark as reviewed" onClick={(e) => { e.stopPropagation(); handleResolve(s); }}>Resolve</button>
                      </ReviewCell>
                    ) : ''}
                  </td>
                  <td>
                    <RowActions>
                      <button title="Edit shift" onClick={(e) => { e.stopPropagation(); openEdit(s); }}><FaEdit /></button>
                      <button className="danger" title="Delete shift" onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}><FaTrash /></button>
                    </RowActions>
                  </td>
                </ClickRow>
              ))}
            </tbody>
          </StyledTable>
          <Pagination
            itemsPerPage={perPage}
            totalItems={total}
            currentPage={page}
            paginate={setPage}
            itemsPerPageOptions={[25, 50, 100, 200]}
            onItemsPerPageChange={(n) => { setPerPage(n); resetPage(); }}
          />
        </TableContainer>
      )}

      <ShiftFormModal
        isOpen={modalOpen}
        mode={modalMode}
        shift={editingShift}
        employees={employees}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      {deleteTarget && (
        <Modal
          isOpen={!!deleteTarget}
          title="Delete Shift"
          message={`Delete this shift for ${deleteTarget.User?.username || `#${deleteTarget.user_id}`}? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMsg}
        success={feedbackSuccess} onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default TimeTrackingReport;
